import client from "./client";
import { getProductionPayErrorMessage } from "./productionPay";

/**
 * 2020CA / 2020NV weekly On Call $ (TAS-475/476).
 *
 * `PUT /admin/onCallPay` is tenant-gated server-side and 404s on every other tenant,
 * so callers must only reach it under `config.onCallPay?.enabled === true`.
 *
 * Business failures (amount <= 0, over the cap, > 2 decimals, role not eligible, week
 * already Payroll Approved) come back as `{ success:false, errorMessage }` — either a 200
 * or a 4xx — so the helper resolves rather than throws; the console reverts and toasts.
 */

/** The on-call job-costing trade: picks the Sage cost code (Plumbing on-call / HVAC on-call). */
export type OnCallTrade = 'PLUMBING' | 'HVAC';

export interface OnCallPayInfo {
  amount: number | null;
  updatedAt: string | null;
  updatedByName: string | null;
  /** Sage job-costing (TAS-475 follow-up). All nullable on legacy rows / when cleared. */
  serviceOrderID?: number | null;
  altServiceOrderID?: string | null;
  /** null on a legacy entry saved before trades existed — must be re-saved before export. */
  trade?: OnCallTrade | null;
  jobAltID?: string | null;
  jobName?: string | null;
  /** Server-resolved Sage cost code for the stored trade (e.g. "40500" / "4150"). */
  costCode?: string | null;
  /** Sage pay type the row exports as (9). */
  payType?: number | string | null;
  /** Configured on-call cost code per trade (server env) — drives the unsaved preview. */
  costCodes?: { PLUMBING?: string; HVAC?: string };
}

/** One of the week's service orders the supervisor can charge the on-call to (most recent first). */
export interface OnCallServiceOrderOption {
  serviceOrderID: number;
  altServiceOrderID: string;
  name: string | null;
  jobAltID: string | null;
  jobName: string | null;
  /** Trade derived from the SO's Sage service type; null when it can't be inferred. */
  derivedTrade: OnCallTrade | null;
  lastWorkDate: string;
}

export interface SetOnCallPayBody {
  employeeID: number;
  payPeriodID: number;
  /** `null` clears (deletes) the week's entry. */
  amount: number | null;
  /** The SO the on-call is charged to; null = no service order. Must be one of the week's options. */
  serviceOrderID?: number | null;
  /** Required when amount > 0. */
  trade?: OnCallTrade | null;
  editSessionID?: string;
}

export interface OnCallPayResult {
  success: boolean;
  errorMessage?: string;
  /** The saved state after a successful write (amount null after a clear). */
  onCallPay?: OnCallPayInfo;
}

/** Upsert (or clear, with `amount: null`) one employee's weekly On Call $. */
export async function setOnCallPay(body: SetOnCallPayBody): Promise<OnCallPayResult> {
  const fallback = "Could not save the On Call amount.";
  try {
    const response = await client.put("/admin/onCallPay", body);
    const data = (response.data ?? {}) as {
      success?: boolean; errorMessage?: string; message?: string; onCallPay?: OnCallPayInfo;
    };
    if (data.success === false) {
      return { success: false, errorMessage: data.errorMessage ?? data.message ?? fallback };
    }
    return data.onCallPay !== undefined
      ? { success: true, onCallPay: data.onCallPay }
      : { success: true };
  } catch (error: unknown) {
    return { success: false, errorMessage: getProductionPayErrorMessage(error, fallback) };
  }
}
